import { useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtCup, fmtUsdt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUIT = { s: "♠", h: "♥", d: "♦", c: "♣" };
export function PlayingCard({ c, small = false, delay = 0 }) {
  const hidden = !c || c === "??";
  const red = !hidden && "hd".includes(c[1]);
  const rank = c?.[0] === "T" ? "10" : c?.[0];
  return (
    <div className={`card-flip ${small ? "w-9 h-13 text-xs" : "w-14 h-20 sm:w-16 sm:h-24 text-base"} rounded-lg shadow-xl flex flex-col items-center justify-center font-bold select-none ${hidden ? "card-back" : "bg-white"} ${red ? "text-rose-600" : "text-slate-900"}`} style={{ animationDelay: `${delay}ms`, height: small ? 52 : undefined }} data-testid={hidden ? "card-hidden" : `card-${c}`}>
      {!hidden && <><span className="leading-none">{rank}</span><span className={`${small ? "text-sm" : "text-2xl"} leading-none`}>{SUIT[c[1]]}</span></>}
    </div>
  );
}

export function Pips({ n }) {
  const pos = { 0: [], 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8], 7: [0, 2, 3, 4, 5, 6, 8], 8: [0, 1, 2, 3, 5, 6, 7, 8], 9: [0, 1, 2, 3, 4, 5, 6, 7, 8] }[n] || [];
  return <div className="grid grid-cols-3 gap-[2px] w-7 h-7 p-[3px]">{[...Array(9)].map((_, i) => <span key={`pip-${i}`} className={`rounded-full ${pos.includes(i) ? "bg-slate-900" : ""}`} />)}</div>;
}

export function DominoTile({ t, vertical = false, onClick, active, testId }) {
  return (
    <button type="button" disabled={!onClick} onClick={onClick} data-testid={testId} className={`tile-pop ${vertical ? "flex-col" : "flex-row"} flex items-center bg-[#f5f1e6] rounded-md shadow-lg border border-slate-300 ${onClick ? "hover:-translate-y-1 hover:shadow-cyan-500/40 cursor-pointer" : ""} ${active ? "ring-2 ring-cyan-400" : ""} transition-transform`}>
      <Pips n={t[0]} /><span className={vertical ? "h-px w-5 bg-slate-400" : "w-px h-5 bg-slate-400"} /><Pips n={t[1]} />
    </button>
  );
}

export function TileBack({ vertical = true }) {
  return <div className={`${vertical ? "w-7 h-14" : "w-14 h-7"} rounded-md bg-gradient-to-br from-slate-700 to-slate-900 border border-slate-600 shadow`} />;
}

export function Seat({ p, isTurn, left, me, children, style }) {
  const pct = isTurn && left != null ? (left / 12) * 100 : 0;
  return (
    <div className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 ${p.folded ? "opacity-50" : ""}`} style={style} data-testid={`seat-${p.id}`}>
      <div className="relative">
        <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-full overflow-hidden flex items-center justify-center font-bold text-lg bg-slate-800 border-2 ${isTurn ? "border-cyan-400 shadow-[0_0_20px_#06B6D4]" : me ? "border-emerald-400" : "border-slate-600"}`}>{p.picture ? <img src={p.picture} alt="" className="h-full w-full object-cover" /> : p.name[0]}</div>
        {isTurn && <svg className="absolute -inset-1 rotate-[-90deg]" viewBox="0 0 36 36"><circle cx="18" cy="18" r="17" fill="none" stroke="#06B6D4" strokeWidth="2" strokeDasharray={`${pct * 1.068} 200`} className="transition-all duration-1000" /></svg>}
        {!p.connected && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-rose-500" title="Desconectado" />}
      </div>
      <div className="bg-slate-900/90 backdrop-blur rounded-lg px-2 py-1 text-center min-w-[84px] border border-slate-700">
        <div className="text-[11px] font-semibold truncate max-w-[90px]">{p.name}{me ? " (tú)" : ""}</div>
        <div className="font-mono text-xs text-amber-300" data-testid={`chips-${p.id}`}>{fmtCup(p.chips)}</div>
        {p.result && <div className="text-[10px] text-emerald-300">{p.result}</div>}
        {p.sitting_out && <div className="text-[10px] text-muted-foreground">esperando</div>}
      </div>
      {children}
    </div>
  );
}

export function seatPos(i, n) {
  const angle = (Math.PI * 2 * i) / n + Math.PI / 2;
  return { left: `${50 + 40 * Math.cos(angle)}%`, top: `${50 + 45 * Math.sin(angle)}%` };
}

export function BuyInDialog({ open, onClose, table, onConfirm, title = "Sentarse en la mesa", multiple = false }) {
  const { user, settings } = useAuth();
  const [amt, setAmt] = useState(table?.buy_in || 0);
  const [cur, setCur] = useState("CUP");
  const usdt = amt / settings.cup_rate;
  const ok = cur === "CUP" ? (user?.balance_cup || 0) >= amt : (user?.balance || 0) >= usdt;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="buyin-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Fichas (CUP) · mínimo {fmtCup(table?.buy_in)}{multiple ? ` · múltiplos de ${fmtCup(table?.buy_in)}` : ""}</Label><Input data-testid="buyin-amount" type="number" step={table?.buy_in} min={table?.buy_in} className="h-12 mt-1 font-mono" value={amt} onChange={(e) => setAmt(Number(e.target.value))} /></div>
          <div className="flex gap-2">{[1, 2, 4].map((k) => <Button key={k} type="button" variant="outline" size="sm" data-testid={`buyin-x${k}`} onClick={() => setAmt(table.buy_in * k)}>×{k}</Button>)}</div>
          <div className="grid grid-cols-2 gap-2 neon-card p-1">
            <button type="button" data-testid="buyin-cup" onClick={() => setCur("CUP")} className={`tap text-sm ${cur === "CUP" ? "bg-emerald-500 text-background" : ""}`}>Pagar {fmtCup(amt)} CUP<div className="text-[10px] font-mono opacity-80">saldo {fmtCup(user?.balance_cup)}</div></button>
            <button type="button" data-testid="buyin-usdt" onClick={() => setCur("USDT")} className={`tap text-sm ${cur === "USDT" ? "bg-cyan-500 text-background" : ""}`}>Pagar {fmtUsdt(usdt)} USDT<div className="text-[10px] font-mono opacity-80">saldo {fmtUsdt(user?.balance)}</div></button>
          </div>
          <p className="text-xs text-muted-foreground">Las fichas valen 1 CUP. Al retirarte se acredita tu saldo en la moneda con que entraste; se descuenta {settings.game_commission_pct ?? 10}% solo sobre la ganancia neta.</p>
          <Button data-testid="buyin-confirm" disabled={!ok || amt < (table?.buy_in || 0)} onClick={() => onConfirm(amt, cur)} className="w-full tap bg-amber-500 text-background hover:bg-amber-400">{ok ? "Confirmar" : "Saldo insuficiente"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TableHeader({ st, me, onLeave, onRebuy }) {
  const mine = st.players.find((p) => p.id === me);
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div><h1 className="font-display text-2xl font-bold">{st.name}</h1><div className="text-xs text-muted-foreground">Entrada {fmtCup(st.buy_in)} CUP · {st.players.length}/{st.max_players} jugadores · Mano #{st.hand_no}{st.queue.length ? ` · ${st.queue.length} en espera` : ""}</div></div>
      <div className="flex gap-2">
        {mine && <Button size="sm" variant="outline" data-testid="rebuy-button" onClick={onRebuy}><Plus size={14} className="mr-1" /> Recomprar</Button>}
        {(mine || st.queue.some((q) => q.user_id === me)) && <Button size="sm" variant="outline" data-testid="leave-button" onClick={onLeave} className="text-rose-300"><LogOut size={14} className="mr-1" /> {mine ? "Retirarme" : "Cancelar reserva"}</Button>}
      </div>
    </div>
  );
}

export function GameLog({ log }) {
  return <div className="neon-card p-3 max-h-40 overflow-auto text-xs space-y-1" data-testid="game-log">{[...log].reverse().map((l, i) => <div key={`${l.t}-${i}`} className={i === 0 ? "text-foreground" : "text-muted-foreground"}>{l.text}</div>)}</div>;
}

export const notifyLeave = (r) => { if (r?.result?.pending) toast.info("Saldrás al terminar la mano"); else if (r?.result) toast.success(`Retiraste ${fmtCup(r.result.chips)} fichas${r.result.fee ? ` (comisión ${fmtCup(r.result.fee)})` : ""}`); };
